import { Button, Card, Field, SelectField, StatusPill, type StatusTone } from '@homecare/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type ReactNode } from 'react';

import { ErrorNote } from '../../components/primitives';
import { API_BASE, useApi } from '../../lib/api-hooks';
import { useHasPermission } from '../../lib/session';

/**
 * Credentials on the caregiver record (§6.3.5, slice-02 §4).
 *
 * ## Expiry is shown before it lapses, not after
 *
 * §17.2's warning thresholds exist because a credential nobody chased is a
 * caregiver who cannot be scheduled next month. The cached status only changes
 * at EXPIRED, by which point the warning is a report — so the row is coloured
 * from `daysUntilExpiry` and `expiringSoon`, which the API computes at read
 * time.
 *
 * ## The status shown here decides nothing
 *
 * `cachedStatus` is what the column says, recomputed nightly, and it is what a
 * person is reading when they scan this list. Whether a caregiver may work on a
 * given date is a different question with a different endpoint —
 * `GET /v1/caregivers/:id/compliance` — and R-2b is that the two must not be
 * confused. The compliance panel above this one is that answer; this is the
 * paperwork.
 */

const STATUS_TONE: Record<string, StatusTone> = {
  PENDING: 'progress',
  VALID: 'verified',
  EXPIRING_SOON: 'warning',
  EXPIRED: 'critical',
  REJECTED: 'critical',
};

const STATUS_LABEL: Record<string, string> = {
  PENDING: 'Awaiting review',
  VALID: 'Verified',
  EXPIRING_SOON: 'Expiring',
  EXPIRED: 'Expired',
  REJECTED: 'Rejected',
};

export interface CredentialRow {
  id: string;
  credentialTypeId: string;
  credentialTypeName: string;
  blocksScheduling: boolean;
  identifier: string | null;
  issuedDate: string | null;
  expiresAt: string | null;
  cachedStatus: string;
  daysUntilExpiry: number | null;
  expiringSoon: boolean;
  hasDocument: boolean;
  rejectionReason: string | null;
}

interface CredentialTypeOption {
  id: string;
  name: string;
  blocksScheduling: boolean;
  isActive: boolean;
}

/**
 * How long is left, in the words somebody would use.
 *
 * "Expires in 12 days" rather than "12", and "Expired 3 days ago" rather than
 * "-3" — a negative number in a column headed *days until expiry* is a puzzle,
 * and the answer is the thing HR is scanning for.
 */
export function describeExpiry(row: {
  expiresAt: string | null;
  daysUntilExpiry: number | null;
}): string {
  if (row.expiresAt === null || row.daysUntilExpiry === null) return 'Does not expire';

  const days = row.daysUntilExpiry;

  if (days < 0) return `Expired ${Math.abs(days)} ${plural(Math.abs(days))} ago`;
  if (days === 0) return 'Expires today';

  return `Expires in ${days} ${plural(days)}`;
}

const plural = (days: number): string => (days === 1 ? 'day' : 'days');

export function CredentialsCard({ caregiverId }: { caregiverId: string }): ReactNode {
  const api = useApi();
  const queryClient = useQueryClient();

  const canRead = useHasPermission('credential:read');
  const canRecord = useHasPermission('credential:update');
  const canVerify = useHasPermission('credential:verify');

  const [adding, setAdding] = useState(false);
  const [rejecting, setRejecting] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [failure, setFailure] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const credentials = useQuery({
    queryKey: ['caregivers', 'credentials', caregiverId],
    queryFn: () =>
      api.get<{ credentials: CredentialRow[] }>(`/v1/caregivers/${caregiverId}/credentials`),
    enabled: canRead,
    // The record is what somebody checks after uploading; 30 seconds of
    // staleness reads as the upload having failed.
    staleTime: 0,
  });

  const types = useQuery({
    queryKey: ['credential-types'],
    queryFn: () => api.get<{ credentialTypes: CredentialTypeOption[] }>('/v1/credential-types'),
    enabled: canRead && adding,
    staleTime: 5 * 60 * 1000,
  });

  if (!canRead) return null;

  const refresh = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['caregivers', 'credentials', caregiverId] }),
      // The compliance answer changes with the paperwork, and a panel that said
      // "blocked: TB test missing" beside a TB test somebody just recorded is
      // the kind of disagreement people stop trusting a screen over.
      queryClient.invalidateQueries({ queryKey: ['caregivers', 'compliance', caregiverId] }),
    ]);
  };

  const decide = async (credentialId: string, action: 'verify' | 'reject'): Promise<void> => {
    setBusy(true);
    setFailure(null);

    // Both paths written out in full rather than assembled from `action`.
    //
    // `.../${credentialId}/${action}` is shorter and it made both endpoints
    // invisible: to `endpoint-reachability.spec.ts`, which reads the client
    // source for the URLs the API serves, and to anyone grepping for who calls
    // `verify`. A URL that only exists at runtime is one nothing can audit.
    try {
      await (action === 'verify'
        ? api.send('POST', `/v1/caregivers/${caregiverId}/credentials/${credentialId}/verify`, {})
        : api.send('POST', `/v1/caregivers/${caregiverId}/credentials/${credentialId}/reject`, {
            reason: rejectReason.trim(),
          }));

      await refresh();
      setRejecting(null);
      setRejectReason('');
    } catch (error) {
      setFailure(error);
    } finally {
      setBusy(false);
    }
  };

  const rows = credentials.data?.credentials ?? [];

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-ink text-lg font-bold">Credentials</h2>
          <p className="text-ink-3 mt-1 text-sm">
            What this caregiver is qualified to do, and when it needs renewing.
          </p>
        </div>

        {canRecord && !adding && (
          <Button variant="secondary" size="sm" onClick={() => setAdding(true)}>
            Record a credential
          </Button>
        )}
      </div>

      <ErrorNote error={failure ?? credentials.error} nextStep="Reload the page and try again." />

      {adding && (
        <RecordCredentialForm
          caregiverId={caregiverId}
          types={(types.data?.credentialTypes ?? []).filter((type) => type.isActive)}
          onDone={async () => {
            await refresh();
            setAdding(false);
          }}
          onCancel={() => setAdding(false)}
        />
      )}

      {credentials.isPending ? (
        <p className="text-ink-3 py-6 text-center text-sm">Loading credentials…</p>
      ) : rows.length === 0 ? (
        <p className="text-ink-3 mt-3 text-sm">
          Nothing on file. A caregiver cannot be scheduled without the credentials their disciplines
          require.
        </p>
      ) : (
        <ul className="mt-4 grid gap-3" data-testid="credential-list">
          {rows.map((row) => (
            <li
              key={row.id}
              className="border-line grid gap-2 rounded-md border p-3"
              data-testid={`credential-${row.credentialTypeId}`}
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-ink text-sm font-semibold">
                  {row.credentialTypeName}
                  {row.blocksScheduling && (
                    <span className="text-ink-3 ml-2 text-xs font-normal">blocks scheduling</span>
                  )}
                </span>

                <StatusPill tone={STATUS_TONE[row.cachedStatus] ?? 'inactive'}>
                  {STATUS_LABEL[row.cachedStatus] ?? row.cachedStatus}
                </StatusPill>
              </div>

              <p
                className={
                  // Coloured from the days remaining, not from the stored
                  // status — which is what makes the warning arrive before the
                  // lapse rather than with it.
                  row.daysUntilExpiry !== null && row.daysUntilExpiry < 0
                    ? 'text-critical text-sm'
                    : row.expiringSoon
                      ? 'text-warning text-sm'
                      : 'text-ink-3 text-sm'
                }
              >
                {describeExpiry(row)}
                {row.expiresAt && <span className="text-ink-3"> · {row.expiresAt}</span>}
                {row.identifier && <span className="text-ink-3"> · {row.identifier}</span>}
              </p>

              {row.rejectionReason && (
                <p className="text-ink-2 text-sm">Rejected: {row.rejectionReason}</p>
              )}

              <div className="flex flex-wrap items-center gap-2">
                {row.hasDocument && (
                  <a
                    className="text-brand text-sm hover:underline"
                    href={`${API_BASE}/v1/caregivers/${caregiverId}/credentials/${row.id}/document`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    View the document
                  </a>
                )}

                {canVerify && row.cachedStatus === 'PENDING' && (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      pending={busy}
                      onClick={() => void decide(row.id, 'verify')}
                    >
                      Verify
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setRejecting(row.id)}>
                      Reject
                    </Button>
                  </>
                )}
              </div>

              {rejecting === row.id && (
                <div className="grid gap-2">
                  <Field
                    label="Why is it being rejected?"
                    // Required, and said here rather than only enforced: a
                    // rejection with no reason is answered by re-uploading the
                    // same unreadable photograph.
                    hint="The caregiver sees this, so say what to send instead."
                    value={rejectReason}
                    onChange={(event) => setRejectReason(event.target.value)}
                    data-testid="reject-reason"
                  />
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      pending={busy}
                      {...(rejectReason.trim()
                        ? {}
                        : ({ disabled: true, disabledReason: 'Give a reason first.' } as const))}
                      onClick={() => void decide(row.id, 'reject')}
                    >
                      Reject this credential
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setRejecting(null)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function RecordCredentialForm({
  caregiverId,
  types,
  onDone,
  onCancel,
}: {
  caregiverId: string;
  types: CredentialTypeOption[];
  onDone: () => Promise<void>;
  onCancel: () => void;
}): ReactNode {
  const api = useApi();

  const [credentialTypeId, setCredentialTypeId] = useState('');
  const [identifier, setIdentifier] = useState('');
  const [issuedDate, setIssuedDate] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);

  const fileInput = useRef<HTMLInputElement>(null);

  const save = async (): Promise<void> => {
    setSaving(true);
    setFailure(null);

    try {
      /*
       * The fields and the document travel together.
       *
       * §17.2 gives the caregiver a direct upload path precisely because "the
       * friction of 'email a photo to the office' is why credentials lapse",
       * and a two-step flow that can half-succeed is friction of the same kind
       * — a credential row with no document, recorded by somebody who believed
       * they had attached one.
       */
      const form = new FormData();
      form.set('credentialTypeId', credentialTypeId);
      form.set('identifier', identifier.trim());
      form.set('issuedDate', issuedDate);
      form.set('expiresAt', expiresAt);
      form.set('file', fileInput.current?.files?.[0] ?? new Blob([]), 'document');

      await api.postForm(`/v1/caregivers/${caregiverId}/credentials`, form);
      await onDone();
    } catch (error) {
      setFailure(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="border-line bg-surface-2 mt-4 grid gap-3 rounded-md border p-4"
      data-testid="credential-form"
    >
      <ErrorNote error={failure} nextStep="Check the dates and try again." />

      <div className="grid gap-3 sm:grid-cols-2">
        <SelectField
          label="Credential"
          value={credentialTypeId}
          onChange={(event) => setCredentialTypeId(event.target.value)}
        >
          <option value="">Choose a credential</option>
          {types.map((type) => (
            <option key={type.id} value={type.id}>
              {type.name}
            </option>
          ))}
        </SelectField>

        <Field
          label="Number"
          hint="The licence or certificate number, if it has one."
          value={identifier}
          onChange={(event) => setIdentifier(event.target.value)}
        />

        <Field
          label="Issued"
          type="date"
          value={issuedDate}
          onChange={(event) => setIssuedDate(event.target.value)}
        />

        <Field
          label="Expires"
          type="date"
          hint="Leave blank if it does not expire — an I-9 does not."
          value={expiresAt}
          onChange={(event) => setExpiresAt(event.target.value)}
        />
      </div>

      <div className="grid gap-1">
        <label className="text-ink text-sm font-medium" htmlFor="credential-document">
          Document
        </label>
        <p className="text-ink-3 text-xs">
          A scan or a photograph. Optional — some credentials are a number from a registry.
        </p>
        <input
          id="credential-document"
          ref={fileInput}
          type="file"
          accept="application/pdf,image/*"
          className="text-ink-2 text-sm"
        />
      </div>

      <div className="flex items-center gap-2">
        <Button
          onClick={() => void save()}
          pending={saving}
          {...(credentialTypeId
            ? {}
            : ({ disabled: true, disabledReason: 'Choose which credential this is.' } as const))}
        >
          Record it
        </Button>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
